"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * About 1057 — KUK home "From Vice Chancellor's Desk".
 *
 * Port of kuk-campus-portal `components/Home/VC_Message.tsx`: a ruled heading
 * over a wide white card carrying a round portrait, name, designation, the
 * message and a "Know More" link, with a stacked pair of tinted vision / mission
 * cards beside it.
 *
 * WHY IT IS AN `about` VARIANT — the app has a `vice_chancellor` section type,
 * but it is scaffolding only: it has no server route, no entry in the public
 * page controller's service map and no widget, and its admin registration is
 * commented out. `about` already carries every field this block needs (heading,
 * portrait, two name lines, body copy, a CTA pair and a `metadata` array) and is
 * fully wired on both the admin and public paths, so no schema or plumbing
 * changes are needed.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px and `lg:` is 1024px. This app remaps `md:` to 1230px and exposes
 * 768px as `tab:` (see globals.css `--breakpoint-*`), so the reference's `md:`
 * is ported as `tab:` and its `lg:` as this app's `sm:` (1080px).
 *
 * THE MESSAGE PARAGRAPHS — v1 splits the description on sentence boundaries and
 * prints ONLY sentences one and two, in two paragraphs, appending a literal "."
 * to the first. Anything from the third sentence on is silently dropped, and a
 * message whose first sentence already ends in "." gets "..". Here the copy is
 * split on newlines instead: the seeded two-sentence message reproduces the
 * live page exactly, and a longer message is no longer truncated.
 *
 * THE SIDE CARDS — v1 gives the FIRST card `h-[40%]` and every other card
 * `h-[60%]`, which sums to the column height for exactly two cards (the shape it
 * ships) and overflows for three or more. The 40/60 split is reproduced for two
 * cards; any other count shares the column evenly instead of overflowing.
 *
 * NOT CARRIED OVER — v1's `popstate` listener, which pushes "/" whenever the
 * visitor presses Back anywhere on the page. That hijacks browser history from
 * a presentational section and is a bug, not a feature.
 */

/** v1's `.custom-underline`: `bottom:-6px; left:0; width:33%; height:5px`. */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

interface VisionCard {
  id?: string | number;
  meta_title?: string;
  meta_icon?: string;
  meta_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface About1057Data {
  /** Section heading. */
  title?: string;
  title_text_color?: string;
  /** Accent rule under the heading. */
  highlight_border_color?: string;
  /** Portrait. */
  desktop_image?: string;
  alt_image?: string;
  /** Name. */
  title_1?: string;
  title_1_color?: string;
  /** Designation. */
  sub_text?: string;
  sub_text_color?: string;
  /** The message. Newlines start a new paragraph. */
  description?: string;
  description_text_color?: string;
  read_more_text?: string;
  read_more_color?: string;
  cta_link?: string;
  /** On = open the lead popup instead of following `cta_link`. */
  is_cta?: boolean;
  /** Portrait card fill / border. */
  gradient_color_2?: string;
  key_highlight_divider_color?: string;
  /** Vision / mission card fill, title and body. */
  gradient_color_1?: string;
  key_highlight_heading_color?: string;
  key_highlight_subtext_color?: string;
  /** Band behind the section. */
  background_color?: string;
  metadata?: VisionCard[];
  status?: boolean;
}

/** v1 `titleCase` — lowercases the whole string, then capitalises each word. */
function titleCase(str: string): string {
  return str
    .toLowerCase()
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const About1057 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as About1057Data | undefined;
  const dispatch = useDispatch();

  const {
    title = "",
    title_text_color = "#031F44",
    highlight_border_color = "#9F1C33",
    desktop_image = "",
    alt_image = "",
    title_1 = "",
    title_1_color = "#031F44",
    sub_text = "",
    sub_text_color = "#64748B",
    description = "",
    description_text_color = "#031F44",
    read_more_text = "Know More",
    read_more_color = "#9F1C33",
    cta_link = "",
    gradient_color_2 = "#FFFFFF",
    key_highlight_divider_color = "#E2E8F0",
    gradient_color_1 = "rgba(159, 28, 51, 0.1)",
    key_highlight_heading_color = "#031F44",
    key_highlight_subtext_color = "#64748B",
    background_color = "#FFFFFF",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);
  const isCta = isCmsShowFlagOn(sectionData?.is_cta, false);

  const paragraphs = useMemo(
    () =>
      (description || "")
        .split(/\r?\n+/)
        .map((p) => p.trim())
        .filter(Boolean),
    [description],
  );

  const cards = useMemo(() => {
    const rows = Array.isArray(sectionData?.metadata) ? sectionData!.metadata! : [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const handleCtaClick = useCallback(() => {
    ctaClick({
      section: "about_1057",
      component: "read_more",
      label: read_more_text?.trim() || "know_more",
      step: "",
      entity: "about_1057",
      parentEntity: "about_1057",
    });
    if (isCta) {
      dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
    }
  }, [dispatch, isCta, read_more_text]);

  if (!status) return null;

  /** v1's 40/60 split holds only for the two cards it ships. */
  const isPair = cards.length === 2;

  return (
    <section className="w-full" style={{ backgroundColor: background_color }}>
      <div className="relative mx-auto w-full max-w-[1120px] overflow-hidden">
        <div className="w-full p-10 tab:p-20">
          {title && (
            <h2
              className="relative w-fit text-2xl font-bold tab:text-4xl"
              style={{ color: title_text_color }}
            >
              {title}
              <span
                aria-hidden
                className={HEADING_UNDERLINE}
                style={{ backgroundColor: highlight_border_color }}
              />
            </h2>
          )}

          <div
            className={cn(
              "mx-auto flex flex-col items-center justify-center gap-4 overflow-hidden",
              "p-0 pt-10 tab:p-10",
              "sm:flex-row sm:gap-0",
            )}
          >
            <div
              className={cn(
                "mx-auto flex min-h-[530px] w-full flex-col flex-nowrap items-center justify-around",
                "rounded-xl border shadow sm:w-[65%]",
              )}
              style={{
                backgroundColor: gradient_color_2,
                borderColor: key_highlight_divider_color,
              }}
            >
              <div className="flex flex-col items-center justify-between space-y-1.5 p-6">
                {desktop_image && (
                  <div className="relative mb-2.5 overflow-hidden">
                    <Image
                      src={desktop_image}
                      alt={alt_image || title_1 || "Vice Chancellor"}
                      width={116}
                      height={116}
                      loading="lazy"
                      className="h-auto rounded-full object-cover"
                    />
                  </div>
                )}
                {title_1 && (
                  <h4
                    className="mt-5 text-base font-bold leading-none tracking-tight"
                    style={{ color: title_1_color }}
                  >
                    {titleCase(title_1)}
                  </h4>
                )}
                {sub_text && (
                  <p className="text-sm" style={{ color: sub_text_color }}>
                    {sub_text}
                  </p>
                )}
              </div>

              {paragraphs.length > 0 && (
                <div
                  className="px-14 pb-0 text-center text-xs leading-7 tab:text-base"
                  style={{ color: description_text_color }}
                >
                  {paragraphs.map((p, i) => (
                    <p
                      key={i}
                      className={cn("text-[15px] font-light", i > 0 && "mt-2")}
                    >
                      {p}
                    </p>
                  ))}
                </div>
              )}

              {read_more_text && (
                <div className="flex items-center p-6 pt-0">
                  {isCta || !cta_link ? (
                    <button
                      type="button"
                      onClick={handleCtaClick}
                      className="text-base font-medium underline-offset-4 hover:underline"
                      style={{ color: read_more_color }}
                    >
                      {read_more_text}
                    </button>
                  ) : (
                    <Link
                      href={normalizeHref(cta_link)}
                      onClick={handleCtaClick}
                      className="text-base font-medium underline-offset-4 hover:underline"
                      style={{ color: read_more_color }}
                    >
                      {read_more_text}
                    </Link>
                  )}
                </div>
              )}
            </div>

            {cards.length > 0 && (
              <div
                className={cn(
                  "flex w-full flex-col items-center justify-between gap-4",
                  "sm:h-[530px] sm:w-[30%]",
                )}
              >
                {cards.map((card, i) => (
                  <div
                    key={card.id ?? i}
                    className={cn(
                      "mx-auto flex w-full flex-col items-start justify-around rounded-xl p-5 shadow",
                      isPair
                        ? i === 0
                          ? "sm:h-[40%]"
                          : "sm:h-[60%]"
                        : "sm:flex-1",
                    )}
                    style={{ backgroundColor: gradient_color_1 }}
                  >
                    <h4
                      className={cn(
                        "mb-5 flex items-center justify-center text-lg font-bold",
                        "tab:mb-0 tab:justify-start",
                      )}
                      style={{ color: key_highlight_heading_color }}
                    >
                      {card.meta_icon && (
                        <span className="mr-3">
                          <DynamicIcon
                            iconName={card.meta_icon}
                            color={key_highlight_heading_color}
                            width="24"
                            height="24"
                          />
                        </span>
                      )}
                      {card.meta_title}
                    </h4>
                    {card.meta_description && (
                      <p
                        className="text-left text-base leading-7"
                        style={{ color: key_highlight_subtext_color }}
                      >
                        {card.meta_description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About1057;
